import { apiBaseURL, APIFetchV2, RequestHeaders, tenant } from "@certa/network";
import type { UserData } from "@certa/common/utils/userStorage";
import { authHeader } from "../../main/src/js/_helpers";
import { intl } from "../../main/src/modules/common/components/IntlCapture";
import { APIFetch } from "../../main/src/js/utils/request";
import { getCaptchaServiceNameforBE } from "main/src/modules/LoginPage/utils/login.utils";
import type {
  LoginCredentials,
  OTPCredentials,
  UpdateSelectedLanguageResponse
} from "@certa/types";

// MFACredentials type removed as it's not currently used
// Will be added back if needed for future MFA enhancements

// Helper function to handle API responses (matches service.ts logic)
async function handleResponse(response: Response) {
  const jsonFromBackend = await response.json();

  /**
   *  When a concurrent user impersonation session already exists
   *  BE should return a status of 434 along with 3 params
   *  { error_tag, impersonator, impersonatee }
   */
  if (response.status === 434) {
    const message = intl.formatMessage(
      {
        id: `impersonation.${jsonFromBackend?.error_tag}`,
        defaultMessage: "An error occurred! Please contact the administrator."
      },
      {
        // eslint-disable-next-line formatjs/enforce-placeholders
        impersonator: jsonFromBackend?.impersonator,
        // eslint-disable-next-line formatjs/enforce-placeholders
        impersonatee: jsonFromBackend?.impersonatee
      }
    );
    throw new Error(message);
  }

  if (!response.ok) {
    throw new Error(jsonFromBackend.detail || "Request failed");
  }
  return jsonFromBackend;
}

export const loginWithEmailPassword = async (credentials: LoginCredentials) => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-DTS-SCHEMA": tenant
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({
      email: credentials.username, // Map username to email as backend expects
      password: credentials.password,
      captcha_token: credentials.captchaToken,
      captcha_service: getCaptchaServiceNameforBE(credentials.captchaService)
    })
  };

  return await APIFetch("users/login/", requestOptions).then(handleResponse);
};

export const loginWithOTP = async (credentials: OTPCredentials) => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-DTS-SCHEMA": tenant !== "test" ? tenant : "vetted"
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({
      email: credentials.username,
      token: credentials.password,
      captcha_token: credentials.captchaToken,
      captcha_service: getCaptchaServiceNameforBE(credentials.captchaService)
    })
  };

  return await APIFetch("users/submit_otp/", requestOptions).then(
    handleResponse
  );
};

export const submitMFAToken = async (pin: string) => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-DTS-SCHEMA": tenant !== "test" ? tenant : "vetted"
    },
    credentials: "include" as RequestCredentials,
    body: JSON.stringify({
      token: pin
    })
  };

  return await APIFetch("users/login_using_mfa/", requestOptions).then(
    handleResponse
  );
};

export const checkAuthV2 = async () => {
  const requestOptions = {
    method: "GET",
    headers: authHeader.get(), // Use proper auth headers like service.ts
    credentials: "include" as RequestCredentials
  };

  return await APIFetch("users/me/?format=json", requestOptions).then(
    handleResponse
  );
};

export const logoutV2 = async () => {
  const requestOptions = {
    method: "GET",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      ...authHeader.get() // Use proper auth headers like service.ts
    },
    credentials: "include" as RequestCredentials
  };

  return await APIFetch("users/logout/", requestOptions).then(handleResponse);
};

export const claimTokenForAutoLoginV2 = async (
  token: string,
  config?: Omit<RequestInit, "method" | "body">
) => {
  const requestOptions: RequestInit = {
    method: "POST",
    body: JSON.stringify({
      token
    }),
    credentials: "include" as RequestCredentials,
    headers: {
      ...RequestHeaders.POST,
      "Content-Type": "application/json",
      "X-DTS-SCHEMA": tenant !== "test" ? tenant : "vetted"
    },
    ...config
  };

  return await APIFetchV2<UserData>(
    "ephemeral-auth/claim-token/",
    requestOptions
  );
};

export const updateSelectedLanguageV2 = async (
  language: string
): Promise<UpdateSelectedLanguageResponse> => {
  const requestOptions = {
    method: "POST",
    headers: authHeader.post(),
    body: JSON.stringify({ to_language: language })
  };

  const url = apiBaseURL + "users/change_prefered_language/";

  return await fetch(url, {
    ...requestOptions,
    credentials: "include"
  }).then(response => {
    if (!response.ok) {
      return Promise.reject(response);
    }
    return response.json();
  });
};
